const VIEWPORT_MARGIN = 12;
const DEFAULT_WIDTH = 760;
const DEFAULT_HEIGHT = 560;
const DEFAULT_MIN_WIDTH = 320;
const DEFAULT_MIN_HEIGHT = 220;

export const initialWorkspaceState = {
  windows: [],
  activeWindowId: null,
  zCounter: 0,
};

const finiteOr = (value, fallback) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export function clampWindowBounds(
  bounds,
  viewport,
  minWidth = DEFAULT_MIN_WIDTH,
  minHeight = DEFAULT_MIN_HEIGHT
) {
  const widthAvailable = Math.max(1, viewport.width - VIEWPORT_MARGIN * 2);
  const heightAvailable = Math.max(1, viewport.height - VIEWPORT_MARGIN * 2);
  const effectiveMinWidth = Math.min(minWidth, widthAvailable);
  const effectiveMinHeight = Math.min(minHeight, heightAvailable);
  const width = clamp(
    finiteOr(bounds.width, DEFAULT_WIDTH),
    effectiveMinWidth,
    widthAvailable
  );
  const height = clamp(
    finiteOr(bounds.height, DEFAULT_HEIGHT),
    effectiveMinHeight,
    heightAvailable
  );
  const maxX = Math.max(
    VIEWPORT_MARGIN,
    viewport.width - width - VIEWPORT_MARGIN
  );
  const maxY = Math.max(
    VIEWPORT_MARGIN,
    viewport.height - height - VIEWPORT_MARGIN
  );

  return {
    x: clamp(finiteOr(bounds.x, VIEWPORT_MARGIN), VIEWPORT_MARGIN, maxX),
    y: clamp(finiteOr(bounds.y, VIEWPORT_MARGIN), VIEWPORT_MARGIN, maxY),
    width,
    height,
  };
}

function createManagedWindow(descriptor, viewport, zIndex, offsetIndex) {
  const width = finiteOr(
    descriptor.bounds?.width,
    Math.min(DEFAULT_WIDTH, viewport.width - 24)
  );
  const height = finiteOr(
    descriptor.bounds?.height,
    Math.min(DEFAULT_HEIGHT, viewport.height - 24)
  );
  const cascade = (offsetIndex % 7) * 24;

  return {
    ...descriptor,
    bounds: clampWindowBounds(
      {
        x: finiteOr(
          descriptor.bounds?.x,
          Math.max(12, (viewport.width - width) / 2 + cascade)
        ),
        y: finiteOr(
          descriptor.bounds?.y,
          Math.max(12, (viewport.height - height) / 2 + cascade)
        ),
        width,
        height,
      },
      viewport,
      descriptor.minWidth || DEFAULT_MIN_WIDTH,
      descriptor.minHeight || DEFAULT_MIN_HEIGHT
    ),
    minWidth: descriptor.minWidth || DEFAULT_MIN_WIDTH,
    minHeight: descriptor.minHeight || DEFAULT_MIN_HEIGHT,
    restoreBounds: null,
    minimized: false,
    maximized: false,
    zIndex,
  };
}

function topVisibleWindowId(windows) {
  const visible = windows.filter(item => !item.minimized);
  if (!visible.length) return null;
  return visible.reduce((top, item) => (item.zIndex > top.zIndex ? item : top))
    .id;
}

function focusWindow(state, id) {
  if (!state.windows.some(item => item.id === id)) return state;
  const zIndex = state.zCounter + 1;
  return {
    ...state,
    zCounter: zIndex,
    activeWindowId: id,
    windows: state.windows.map(item =>
      item.id === id ? { ...item, minimized: false, zIndex } : item
    ),
  };
}

export function workspaceReducer(state, action) {
  switch (action.type) {
    case 'open': {
      const existing = state.windows.find(item => item.id === action.window.id);
      if (existing) {
        const focused = focusWindow(state, action.window.id);
        return {
          ...focused,
          windows: focused.windows.map(item =>
            item.id === action.window.id
              ? {
                  ...item,
                  ...action.window,
                  bounds: item.bounds,
                  restoreBounds: item.restoreBounds,
                }
              : item
          ),
        };
      }

      const zIndex = state.zCounter + 1;
      const managedWindow = createManagedWindow(
        action.window,
        action.viewport,
        zIndex,
        state.windows.length
      );
      return {
        windows: [...state.windows, managedWindow],
        activeWindowId: managedWindow.id,
        zCounter: zIndex,
      };
    }
    case 'focus':
      return focusWindow(state, action.id);
    case 'close': {
      const windows = state.windows.filter(item => item.id !== action.id);
      return {
        ...state,
        windows,
        activeWindowId:
          state.activeWindowId === action.id
            ? topVisibleWindowId(windows)
            : state.activeWindowId,
      };
    }
    case 'minimize': {
      const windows = state.windows.map(item =>
        item.id === action.id ? { ...item, minimized: true } : item
      );
      return {
        ...state,
        windows,
        activeWindowId:
          state.activeWindowId === action.id
            ? topVisibleWindowId(windows)
            : state.activeWindowId,
      };
    }
    case 'maximize': {
      const target = state.windows.find(item => item.id === action.id);
      if (!target) return state;
      const zIndex = state.zCounter + 1;
      return {
        windows: state.windows.map(item =>
          item.id === action.id
            ? {
                ...item,
                restoreBounds: item.maximized
                  ? item.restoreBounds
                  : item.bounds,
                bounds: clampWindowBounds(
                  {
                    x: VIEWPORT_MARGIN,
                    y: VIEWPORT_MARGIN,
                    width: action.viewport.width - VIEWPORT_MARGIN * 2,
                    height: action.viewport.height - VIEWPORT_MARGIN * 2,
                  },
                  action.viewport,
                  item.minWidth,
                  item.minHeight
                ),
                minimized: false,
                maximized: true,
                zIndex,
              }
            : item
        ),
        activeWindowId: action.id,
        zCounter: zIndex,
      };
    }
    case 'restore': {
      const target = state.windows.find(item => item.id === action.id);
      if (!target) return state;
      const zIndex = state.zCounter + 1;
      return {
        windows: state.windows.map(item =>
          item.id === action.id
            ? {
                ...item,
                bounds: clampWindowBounds(
                  item.restoreBounds || item.bounds,
                  action.viewport,
                  item.minWidth,
                  item.minHeight
                ),
                restoreBounds: null,
                minimized: false,
                maximized: false,
                zIndex,
              }
            : item
        ),
        activeWindowId: action.id,
        zCounter: zIndex,
      };
    }
    case 'move':
      return {
        ...state,
        windows: state.windows.map(item =>
          item.id === action.id && !item.maximized
            ? {
                ...item,
                bounds: clampWindowBounds(
                  { ...item.bounds, x: action.x, y: action.y },
                  action.viewport,
                  item.minWidth,
                  item.minHeight
                ),
              }
            : item
        ),
      };
    case 'resize':
      return {
        ...state,
        windows: state.windows.map(item =>
          item.id === action.id && !item.maximized
            ? {
                ...item,
                bounds: clampWindowBounds(
                  {
                    ...item.bounds,
                    width: action.width,
                    height: action.height,
                  },
                  action.viewport,
                  item.minWidth,
                  item.minHeight
                ),
              }
            : item
        ),
      };
    case 'reflow':
      return {
        ...state,
        windows: state.windows.map(item => ({
          ...item,
          bounds: item.maximized
            ? clampWindowBounds(
                {
                  x: VIEWPORT_MARGIN,
                  y: VIEWPORT_MARGIN,
                  width: action.viewport.width - VIEWPORT_MARGIN * 2,
                  height: action.viewport.height - VIEWPORT_MARGIN * 2,
                },
                action.viewport,
                item.minWidth,
                item.minHeight
              )
            : clampWindowBounds(
                item.bounds,
                action.viewport,
                item.minWidth,
                item.minHeight
              ),
        })),
      };
    default:
      return state;
  }
}
